import { createContext, useCallback, useEffect, useState } from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Lockup } from "../components/Lockup";
import Marks from "./Marks";
import TheUnderwriter from "./TheUnderwriter";
import ComparableEvidence from "./ComparableEvidence";
import TenantRegistry from "./TenantRegistry";
import TheGatekeeper from "./TheGatekeeper";
import TheSteward from "./TheSteward";
import TheFloorManager from "./TheFloorManager";
import TheLibrarian from "./TheLibrarian";
import TheChiefOfStaff from "./TheChiefOfStaff";
import TheCommandCenter from "./TheCommandCenter";
import TheTreasurer from "./TheTreasurer";
import TheListener from "./TheListener";
import Rainmaker from "./Rainmaker";

// One console, one URL, desktop-first. Console v2 (founder brief 30 Sep) takes the ADMIN DESIGN'S
// LAYOUT and keeps production's function: the staff card on top, then the Command Center, then the
// two groups the founder named — SIX RUN THE PLATFORM, THREE RUN THE COMPANY — each item with the
// design's icon and production's own sub-items indented under their agent. Badges are REAL or ABSENT:
// they are the Command Center's `needs_human` items grouped by agent, so an amber badge always means
// a person is needed and a zero never renders. A section a staff member has no scope for is not shown
// (production's rule). Sign out and the build hash stay at the bottom. No Settings (ruling, Part 2).
const ICON = {
  home: "M4 4h7v6H4zM13 4h7v9h-7zM4 14h7v6H4zM13 17h7v3h-7z",
  librarian: "M4 6h11l3 3v9H4zM4 12h4a2 2 0 004 0h8",
  underwriter: "M3 3v18h18M7 15l3-4 3 3 4-6",
  floor_manager: "M6 4v5M6 15v5M4 12h4M18 4v3M18 13v7M16 10h4",
  steward: "M3 7h18v10H3zM12 10a2 2 0 100 4 2 2 0 100-4",
  gatekeeper: "M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6zM9 12l2 2 4-4",
  listener: "M4 11v2M8 8v8M12 5v14M16 9v6M20 11v2",
  treasurer: "M3 20h18M7 20v-6M12 20V8M17 20v-10",
  rainmaker: "M3 17l6-6 4 4 7-7M16 8h5v5",
  chief_of_staff: "M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7M3 20v-1.5A4.5 4.5 0 017.5 14h3A4.5 4.5 0 "
    + "0 1 15 18.5V20M16 4.5a3.5 3.5 0 010 6.5M21 20v-1.5a4.5 4.5 0 00-3-4.2",
};

// [scope, agent name, route, testid, sub-items]. `scope` null = no scope gate (the Command Center is
// the cross-agent front door and owns no scope — R1-CC-06).
const PLATFORM = [
  ["librarian", "The Librarian", "/console/librarian", "nav-librarian",
   [["Tenant Registry", "/console/librarian/tenant-registry", "nav-tenant-registry"]]],
  ["underwriter", "The Underwriter", "/console/underwriter/desk", "nav-desk",
   [["Marks", "/console/underwriter/marks", "nav-marks"],
    ["Comparable Evidence", "/console/underwriter/evidence", "nav-evidence"]]],
  ["floor_manager", "The Floor Manager", "/console/floor-manager", "nav-floor-manager", []],
  ["steward", "The Steward", "/console/steward", "nav-steward", []],
  ["gatekeeper", "The Gatekeeper", "/console/gatekeeper", "nav-gatekeeper", []],
  ["listener", "The Listener", "/console/listener", "nav-listener", []],
];

const COMPANY = [
  ["treasurer", "The Treasurer", "/console/treasurer", "nav-treasurer", []],
  ["rainmaker", "The Rainmaker", "/console/rainmaker", "nav-rainmaker", []],
  ["chief_of_staff", "The Chief of Staff", "/console/chief-of-staff", "nav-chief-of-staff", []],
];

// The agent name the Command Center files an item under → the rail's scope key.
const AGENT_SCOPE = {
  "The Librarian": "librarian", "The Underwriter": "underwriter",
  "The Floor Manager": "floor_manager", "The Steward": "steward",
  "The Gatekeeper": "gatekeeper", "The Listener": "listener",
  "The Treasurer": "treasurer", "The Rainmaker": "rainmaker",
  "The Chief of Staff": "chief_of_staff",
};

// ONE definition of "waiting on a person" for the whole console (founder ruling 30 Sep): the rail's
// amber badge and the section's own headline read the SAME number from here, so a screen can never say
// 1 and 5 about itself. The number is the Command Center's `needs_human` items filed under that agent
// — real collections only, no dev fixtures. `loaded` matters: until the count has arrived a section
// must not claim a needs-a-human figure, so it says what its own queue holds instead.
export const Waiting = createContext({ waiting: {}, loaded: false });

function NavIcon({ d }) {
  return (
    <svg className="rail-icon" width="16" height="16" viewBox="0 0 24 24" fill="none"
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

function RailItem({ scope, name, to, testid, subs, end, waiting }) {
  return (
    <div className="rail-group-item">
      <NavLink to={to} end={end} data-testid={testid} className="rail-item">
        <NavIcon d={ICON[scope] || ICON.home} />
        <span className="rail-label">{name}</span>
        {waiting > 0 && (
          <span className="rail-badge rail-badge-amber" data-testid={`${testid}-badge`}
                title={`${waiting} waiting on a person`}>{waiting}</span>
        )}
      </NavLink>
      {subs.map(([label, subTo, subTestid]) => (
        <NavLink key={subTo} to={subTo} data-testid={subTestid} className="rail-item rail-sub">
          <span className="rail-label">{label}</span>
        </NavLink>
      ))}
    </div>
  );
}

export default function Shell() {
  const { session, authedFetch, logout } = useAuth();
  const [who, setWho] = useState(null);
  const [waiting, setWaiting] = useState({});
  const [waitingLoaded, setWaitingLoaded] = useState(false);

  const load = useCallback(() => {
    // Real account data only — no placeholder person, and no card at all until it loads.
    authedFetch("/identity/staff/me").then(setWho).catch(() => setWho(null));
    // Real badge counts only. The Command Center already decides what needs a human across the
    // agents; grouping ITS items keeps one definition of "waiting on you" for the whole console.
    authedFetch("/command-center/section")
      .then((d) => {
        const by = {};
        (d?.needs_human?.items || []).forEach((it) => {
          const k = AGENT_SCOPE[it.agent];
          if (k) by[k] = (by[k] || 0) + 1;
        });
        setWaiting(by);
        setWaitingLoaded(true);
      })
      .catch(() => { setWaiting({}); setWaitingLoaded(false); });
  }, [authedFetch]);
  useEffect(load, [load]);

  const held = new Set(who?.scopes || session?.scopes || []);
  const canSee = (scope) => !scope || held.has(scope);
  const platform = PLATFORM.filter(([s]) => canSee(s));
  const company = COMPANY.filter(([s]) => canSee(s));
  const seat = who?.is_master ? "MASTER SEAT · EVERY QUEUE"
    : (who?.scopes || []).length ? `${who.scopes.length} of 9 queues` : null;

  return (
    <div className="shell" data-testid="console-shell">
      <aside className="sidebar">
        <div className="rail-brand">
          <Lockup size="rail" />
          <span className="rail-admin-chip" data-testid="rail-admin-chip">ADMIN</span>
        </div>

        {who && (
          <div className="rail-who" data-testid="rail-staff-card">
            <span className="rail-av" aria-hidden="true">{who.initials}</span>
            <span className="rail-who-text">
              <span className="rail-who-name" data-testid="rail-staff-name">{who.name}</span>
              <span className="rail-who-email" data-testid="rail-staff-email">{who.email}</span>
            </span>
          </div>
        )}
        {who && (
          <div className="rail-scopes" data-testid="rail-staff-scopes">
            {seat && <span className="rail-seat-chip">{seat}</span>}
            {!who.is_master && (
              <span className="rail-scope-list">{(who.scopes || []).join(" · ") || "no queues yet"}</span>
            )}
          </div>
        )}

        <nav className="rail-nav">
          <RailItem scope="home" name="The Command Center" to="/console" testid="nav-command-center"
                    subs={[]} end waiting={0} />

          {platform.length > 0 && (
            <div className="rail-group-head" data-testid="rail-group-platform">
              <span>{platform.length === 6 ? "Six run the platform" : "They run the platform"}</span>
              <span className="rail-rule" />
            </div>
          )}
          {platform.map(([scope, name, to, testid, subs]) => (
            <RailItem key={to} scope={scope} name={name} to={to} testid={testid} subs={subs}
                      waiting={waiting[scope] || 0} />
          ))}

          {company.length > 0 && (
            <div className="rail-group-head" data-testid="rail-group-company">
              <span>{company.length === 3 ? "Three run the company" : "They run the company"}</span>
              <span className="rail-rule" />
            </div>
          )}
          {company.map(([scope, name, to, testid, subs]) => (
            <RailItem key={to} scope={scope} name={name} to={to} testid={testid} subs={subs}
                      waiting={waiting[scope] || 0} />
          ))}
        </nav>

        <div className="sidebar-foot">
          <button className="rail-signout" data-testid="sign-out" onClick={() => logout()}>
            <span className="rail-signout-dot" aria-hidden="true" />Sign out
          </button>
          <div className="who" data-testid="whoami">
            {session?.account_id ? session.account_id.slice(0, 8) : "staff"}
          </div>
        </div>
      </aside>
      <main className="content">
        <Waiting.Provider value={{ waiting, loaded: waitingLoaded }}>
        <Routes>
          <Route index element={<TheCommandCenter />} />
          <Route path="underwriter/desk" element={<TheUnderwriter />} />
          <Route path="underwriter/marks" element={<Marks />} />
          <Route path="underwriter/evidence" element={<ComparableEvidence />} />
          <Route path="gatekeeper" element={<TheGatekeeper />} />
          <Route path="steward" element={<TheSteward />} />
          <Route path="floor-manager" element={<TheFloorManager />} />
          <Route path="librarian" element={<TheLibrarian />} />
          <Route path="librarian/tenant-registry" element={<TenantRegistry />} />
          <Route path="chief-of-staff" element={<TheChiefOfStaff />} />
          <Route path="rainmaker" element={<Rainmaker />} />
          <Route path="treasurer" element={<TheTreasurer />} />
          <Route path="listener" element={<TheListener />} />
          <Route path="*" element={<EmptyState title="Not built yet"
            body="This section has not been specified. It will appear when its agent is built." />} />
        </Routes>
        </Waiting.Provider>
      </main>
    </div>
  );
}

export function EmptyState({ title, body }) {
  return (
    <div className="empty-state" data-testid="empty-state">
      <h2>{title}</h2>
      <p>{body}</p>
    </div>
  );
}
